import { Delete } from '@mui/icons-material';
import { styled, Tooltip } from '@mui/material';
import Button from 'components/Button';
import ConfirmDialog from 'further-ui/components/ConfirmDialog';
import { Row } from 'further-ui/components/Layout';
import { useDisclosure } from 'further-ui/hooks';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import api from 'lib/trpcClient';

const DeleteButton = styled(Button)(({ theme }) => ({
  padding: 0,
  marginBottom: theme.spacing(theme.spacingSizes.xs),
}));

type Props = {
  vehicleId: string;
  bankAccountId: string;
  bankOperationId: string;
};

const DeleteBankOperation: React.FC<Props> = ({
  vehicleId,
  bankAccountId,
  bankOperationId,
}) => {
  const deleteDisclosure = useDisclosure();

  const { data: canDeleteData } =
    api.bankAccount.canDeleteBankAccountOperations.useQuery({
      vehicleId,
      bankAccountId,
    });

  const deleteOperations =
    api.bankAccount.deleteBankAccountOperations.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Bank operation deleted.',
      }),
    );

  const handleDeleteBankOperation = () => {
    deleteOperations.mutate({
      vehicleId,
      bankAccountId,
      operationIds: [bankOperationId],
    });
    deleteDisclosure.onClose();
  };

  const canDelete = canDeleteData?.canDelete ?? true;
  const deleteDisabledReason = canDeleteData?.reason;

  const deleteButton = (
    <DeleteButton
      size="small"
      color="error"
      onClick={deleteDisclosure.onOpen}
      disabled={!canDelete}
    >
      <Delete color="error" fontSize="small" />
      Delete transaction
    </DeleteButton>
  );

  return (
    <Row justify="flex-end">
      {!canDelete && deleteDisabledReason ? (
        <Tooltip title={deleteDisabledReason}>
          <span>{deleteButton}</span>
        </Tooltip>
      ) : (
        deleteButton
      )}
      <ConfirmDialog
        open={deleteDisclosure.isOpen}
        onClose={deleteDisclosure.onClose}
        title="Are you sure you want to delete this transaction?"
        content="This action cannot be undone."
        maxWidth="sm"
        actions={{
          confirm: {
            onClick: handleDeleteBankOperation,
            label: 'Delete',
            color: 'error',
          },
        }}
      />
    </Row>
  );
};

export default DeleteBankOperation;
